import { useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { useSnackbar } from "../components/utils/SnackbarProvider";
import apiRoutes from "./routes";
import routes from "../constants/routes";
import { useUser } from "../context/user-context";

const useApiRequest = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar(); // Use the global Snackbar
  const location = useLocation(); // Get the current location (route)
  // const storedUser = JSON.parse(localStorage.getItem("user"));
  const { user, updateUser } = useUser();

  const handleUnauthorized = () => {
    if (location.pathname !== "/login" && location.pathname !== "/") {
      localStorage.removeItem("user");
      updateUser(null);
      navigate(0);
    }
  };
  const makeApiCall = useCallback(
    async (
      endpoint,
      method,
      body = null,
      showToast = "hide",
      headers = {},
      newToken
    ) => {
      const refreshAccessToken = async () => {
        try {
          const refreshResponse = await fetch(
            `${process.env.REACT_APP_BASE_URL}${apiRoutes.auth.refresh}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.refreshToken}`,
              },
            }
          );

          if (!refreshResponse.ok) {
            throw new Error(`Error: ${refreshResponse.success}`);
          }
          const refreshData = await refreshResponse.json();

          if (
            refreshData.success === false ||
            refreshData.statusCode === 400 ||
            !refreshData
          ) {
            handleUnauthorized();
          } else {
            return refreshData.newAccessToken;
          }
        } catch (error) {
          handleUnauthorized();
          console.error("Token refresh failed:", error);
          return null;
        }
      };

      // Prepare request options
      const options = {
        method,
        headers: {
          "Content-Type": "application/json",
          ...headers,
          Authorization: newToken
            ? `Bearer ${newToken}`
            : user?.authToken
            ? `Bearer ${user?.authToken}`
            : undefined,
        },
        body: body ? JSON.stringify(body) : undefined,
      };
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}${endpoint}`,
          options
        );
        const responseData = await response.json();

        if (responseData.statusCode === 401) {
          if (responseData.data) {
            const newToken = await refreshAccessToken();
            if (newToken) {
              return await makeApiCall(
                endpoint,
                method,
                body,
                "hide",
                headers,
                newToken
              );
            } else {
              handleUnauthorized();
            }
          } else {
            handleUnauthorized();
          }

          return responseData; // Return the response data if needed
        }
        if (responseData.statusCode === 404) {
          navigate("/404", { state: { message404: responseData.message } });
        }
        if (responseData.statusCode === 500) {
          navigate("/5xx", { state: { message5xx: responseData.message } });
        }
        if (responseData.statusCode === 400) {
          showSnackbar(responseData.message, "error");
        }
        if (showToast === "show") {
          if (responseData.statusCode === 200) {
            showSnackbar(responseData.message, "success"); // Show success snackbar
          } else {
            showSnackbar(responseData.message, "error"); // Show success snackbar
          }
        }

        return responseData;
      } catch (error) {
        if (error.message.toLower === "load failed") {
          showSnackbar(
            "Please check your internet connection or try again later",
            "error"
          );
        } else {
          showSnackbar(error.message, "error");
        }
      }
    },
    [navigate, user, location.pathname, updateUser]
  );

  return { makeApiCall };
};

export default useApiRequest;
